import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Link from '../Link';

const isBrowser = typeof window !== 'undefined';

const addLead = (data) => {
  const addLeadPostUrl =
    process.env.NODE_ENV === 'development'
      ? 'https://pielaco.app.n8n.cloud/webhook-test/5e650801-9c2a-4604-8cda-c0ab2156a5ae'
      : 'https://pielaco.app.n8n.cloud/webhook/5e650801-9c2a-4604-8cda-c0ab2156a5ae';

  return axios.post(addLeadPostUrl, data);
};

export default function LeadgenForm({
  title,
  slug,
  leadOwner,
  downloadUrl,
  callToAction,
  privacyPolicyLink,
}) {
  const [submitStatus, setSubmitStatus] = useState({
    formSubmitted: false,
    success: false,
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      company: '',
      email: '',
    },
  });

  // Tracking Logic
  const trackPlausible = (eventName, extraProps = {}) => {
    if (isBrowser && window.plausible) {
      window.plausible(eventName, { 
        props: { title, ...extraProps } 
      });
    }
  };

  const saveAccess = () => {
    if (isBrowser) localStorage.setItem(slug, 'true');
  };

  const hasAccess = () => {
    return (
      isBrowser &&
      (localStorage.getItem(slug) === 'true' ||
        (submitStatus.formSubmitted && submitStatus.success))
    );
  };

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      title,
      leadOwner,
    };

    try {
      // Axios throws an error if the status is not 2xx.
      await addLead(payload);

      trackPlausible('Download');
      
      // We assume success because we are in the try block
      setSubmitStatus({ formSubmitted: true, success: true });
      
      saveAccess();
      
      // Optional: Check if downloadUrl exists before opening
      if (downloadUrl) {
        window.open(downloadUrl);
      }

    } catch (error) {
      trackPlausible('Download Error', { error });
      setSubmitStatus({ formSubmitted: true, success: false });
    }
  };

  const submitFailed = submitStatus.formSubmitted && !submitStatus.success;

  return (
    <Box sx={{ padding: 3, backgroundColor: 'background.paper' }}>
      <Typography variant="h5" gutterBottom>
        {callToAction}
      </Typography>

      {!hasAccess() && !submitFailed && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Vor- und Nachname"
                  variant="standard"
                  fullWidth
                  required
                />
              )}
            />
            <Controller
              name="company"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Unternehmen"
                  variant="standard"
                  fullWidth
                  required
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              rules={{ 
                required: true, 
                pattern: /^\S+@\S+$/i 
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="E-Mail-Adresse"
                  variant="standard"
                  fullWidth
                  required
                />
              )}
            />

            <Typography variant="body2">
              Wir hoffen, dass die Unterlagen hilfreich sind. Unter Umständen
              würden wir dich persönlich kontaktieren, um zu erörtern, ob wir
              darüber hinaus hilfreich sein können. Siehe auch{' '}
              <Link to={privacyPolicyLink}>Datenschutz</Link>.
            </Typography>

            <Button
              type="submit"
              variant="text"
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? 'Wird gesendet...' : callToAction}
            </Button>
          </Stack>
        </form>
      )}

      {hasAccess() && (
        <Stack spacing={2}>
          <Typography>
            Vielen Dank, dass du dich für unsere Arbeit und die unserer
            Partner interessierst!
          </Typography>
          <Typography>Lade nun dein Dokument hier herunter:</Typography>
          <Button
            variant="text"
            sx={{ width: '100%' }}
            onClick={() => window.open(downloadUrl)}
          >
            {callToAction}
          </Button>
        </Stack>
      )}

      {submitFailed && (
        <Typography color="error">Es gab leider einen Fehler :(</Typography>
      )}
    </Box>
  );
}